import { useEffect, useMemo, useState } from "react";
import { useRoute } from "wouter";
import {
  Check,
  ChevronDown,
  Copy,
  Loader2,
  MapPin,
  Plus,
  Tag,
  UserCircle2,
  Users,
  X,
} from "lucide-react";
import { useAppNavigation } from "@/lib/platform";
import { useToast } from "@/components/ux/Toast";
import {
  useCreateHostEvent,
  useHostEvent,
  useUpdateHostEvent,
  type CreateHostEventRequest,
} from "../api/useHostEvents";
import { useCustomEvents } from "@/lib/features";
import { useMyGroups } from "@/lib/api/useGroups";
import { CoverImageUpload } from "@/components/CoverImageUpload";
import { CitySelectModal } from "@/components/CitySelectModal";
import { EventTypeSelectModal } from "@/components/EventTypeSelectModal";
import { LocationSelectModal } from "../components/LocationSelectModal";
import { RichTextEditor } from "../components/RichTextEditor";
import { useClosestCity } from "../api/useLocationSearch";
import {
  MAX_EVENT_TYPES_PER_EVENT,
  getEventType,
} from "@shared/eventTypes";
import type { EventLink } from "@shared/eventLinks";
import { LinksEditor } from "../components/LinksEditor";

// Consumer-facing host event creation. The page reads like a poster being
// filled in — title is huge and borderless, when/where/tags are inline
// tappable rows anchored by an icon, and only the "Options" group (capacity,
// price, age, visibility) retains a card with form chrome. Sticky submit at
// the bottom.

// Pill-shaped read-only-looking inputs/buttons used in the Start/End time
// card — bg-fill, rounded full, small inset padding. Native date/time inputs
// pick up the right color-scheme for dark mode via [color-scheme:dark].
const PILL_FIELD =
  "bg-base-200 hover:bg-base-300 px-4 py-1.5 rounded-full text-sm font-medium border-0 outline-none focus:outline-none focus:ring-2 focus:ring-primary-400 transition-colors [color-scheme:dark]";

// Standardized divider — slightly contrasts the card gradient in both
// themes. black/10 over the light gradient = soft grey line; white/10 over
// the dark gradient = soft light line. Used for any divider inside a card.
const DIVIDER = "border-t border-black/10 dark:border-white/10";

type FormState = {
  name: string;
  description: string;
  types: string[];
  date: string;
  time: string;
  endTime: string;
  cityId: string;
  cityName: string;
  cityEmoji: string;
  venueName: string;
  venueAddress: string;
  venueLat: number | null;
  venueLng: number | null;
  venuePlaceId: string;
  // "" = no limit; numeric string otherwise
  capacity: string;
  // "" = not specified, "free" = explicitly free, "paid" = paid (use priceDollars)
  pricing: "" | "free" | "paid";
  priceDollars: string;
  visibility: "public" | "unlisted";
  addressRevealPolicy: "immediately" | "on_rsvp" | "hours_before";
  addressRevealHoursBefore: string;
  ageMin: string;
  ageMax: string;
  coverImageUrl: string;
  links: EventLink[];
  groupId: string | null;
  // Set when an admin creates a club's in-person event from /admin/clubs.
  clubId: string | null;
};

const initialState: FormState = {
  name: "",
  description: "",
  types: [],
  date: "",
  time: "",
  endTime: "",
  cityId: "",
  cityName: "",
  cityEmoji: "",
  venueName: "",
  venueAddress: "",
  venueLat: null,
  venueLng: null,
  venuePlaceId: "",
  capacity: "",
  pricing: "",
  priceDollars: "",
  visibility: "public",
  addressRevealPolicy: "immediately",
  addressRevealHoursBefore: "24",
  ageMin: "",
  ageMax: "",
  coverImageUrl: "",
  links: [],
  groupId: null,
  clubId: null,
};

function toRequestBody(s: FormState): CreateHostEventRequest {
  const ageMin = s.ageMin ? Number(s.ageMin) : undefined;
  const ageMax = s.ageMax ? Number(s.ageMax) : undefined;
  const audience =
    ageMin != null || ageMax != null ? { ageMin, ageMax } : undefined;
  return {
    name: s.name.trim(),
    description: s.description.trim() || undefined,
    types: s.types,
    date: s.date,
    time: s.time,
    endTime: s.endTime || null,
    cityId: s.cityId,
    venueName: s.venueName.trim(),
    venueAddress: s.venueAddress.trim(),
    venueLat: s.venueLat,
    venueLng: s.venueLng,
    venuePlaceId: s.venuePlaceId || undefined,
    capacity: s.capacity ? Number(s.capacity) : null,
    priceCents:
      s.pricing === "free"
        ? 0
        : s.pricing === "paid" && s.priceDollars
          ? Math.round(parseFloat(s.priceDollars) * 100)
          : null,
    visibility: s.visibility,
    audienceFilter: audience,
    addressRevealPolicy: s.addressRevealPolicy,
    addressRevealHoursBefore:
      s.addressRevealPolicy === "hours_before"
        ? Number(s.addressRevealHoursBefore)
        : undefined,
    coverImageUrl: s.coverImageUrl.trim() || undefined,
    links: s.links,
    groupId: s.groupId ?? null,
    ...(s.clubId ? { clubId: s.clubId } : {}),
  };
}

// ---------------------------------------------------------------------------

export default function HostEventCreate() {
  // Edit mode is keyed off the route — `/host/events/:slug/edit`. When
  // matched, we fetch the existing event, pre-fill the form, and submit via
  // the update endpoint instead of create.
  const [, editParams] = useRoute("/events/:slug/edit");
  const editSlug = editParams?.slug ?? null;
  const isEditMode = !!editSlug;

  const customEventsEnabled = useCustomEvents();
  const { data: myGroupsData } = useMyGroups();
  const myGroups = useMemo(
    () => (myGroupsData?.success ? (myGroupsData.data ?? []) : []),
    [myGroupsData],
  );
  const [form, setForm] = useState<FormState>(initialState);
  const [prefilled, setPrefilled] = useState(false);
  const [created, setCreated] = useState<{ slug: string; shareUrl: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [cityModalOpen, setCityModalOpen] = useState(false);
  const [typeModalOpen, setTypeModalOpen] = useState(false);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [groupPickerOpen, setGroupPickerOpen] = useState(false);
  const toast = useToast();
  const { navigate } = useAppNavigation();
  const create = useCreateHostEvent();
  const update = useUpdateHostEvent(editSlug ?? "");
  const existing = useHostEvent(editSlug);
  const closestCity = useClosestCity();
  const existingData = existing.data?.success ? existing.data.data : null;

  // Pre-fill once when the existing event loads.
  useEffect(() => {
    if (!isEditMode || prefilled || !existingData) return;
    const af = existingData.audienceFilter;
    setForm({
      name: existingData.name,
      description: existingData.description ?? "",
      types: existingData.types ?? [],
      date: existingData.date,
      time: existingData.time,
      endTime: existingData.endTime ?? "",
      cityId: existingData.city?.id ?? "",
      cityName: existingData.city?.name ?? "",
      cityEmoji: "",
      venueName: existingData.venue.name,
      venueAddress: existingData.venue.address,
      venueLat: existingData.venue.lat,
      venueLng: existingData.venue.lng,
      venuePlaceId: existingData.venue.placeId ?? "",
      capacity:
        existingData.capacity != null ? String(existingData.capacity) : "",
      pricing:
        existingData.priceCents == null
          ? ""
          : existingData.priceCents === 0
            ? "free"
            : "paid",
      priceDollars:
        existingData.priceCents != null && existingData.priceCents > 0
          ? (existingData.priceCents / 100).toString()
          : "",
      visibility: (existingData.visibility as FormState["visibility"]) ?? "public",
      addressRevealPolicy: existingData.addressRevealPolicy,
      addressRevealHoursBefore:
        existingData.addressRevealHoursBefore != null
          ? String(existingData.addressRevealHoursBefore)
          : "24",
      ageMin: af?.ageMin != null ? String(af.ageMin) : "",
      ageMax: af?.ageMax != null ? String(af.ageMax) : "",
      coverImageUrl: existingData.coverImageUrl ?? "",
      links: existingData.links ?? [],
      groupId: existingData.groupId ?? null,
      // Club links are only set on create; edits leave them untouched.
      clubId: null,
    });
    setPrefilled(true);
  }, [isEditMode, prefilled, existingData]);

  // When arriving from a group page ("Add event"), preselect that group as the
  // host identity. Only applies on create, and only if the group is one of ours.
  const [groupPrefilled, setGroupPrefilled] = useState(false);
  useEffect(() => {
    if (isEditMode || groupPrefilled || myGroups.length === 0) return;
    const groupId = new URLSearchParams(window.location.search).get("group");
    if (groupId && myGroups.some((g) => g.id === groupId)) {
      setForm((f) => ({ ...f, groupId }));
    }
    setGroupPrefilled(true);
  }, [isEditMode, groupPrefilled, myGroups]);

  // When arriving from an admin club page ("Create event" for a ready city),
  // link the event to the club and preselect the city. The server only
  // accepts clubId from admins.
  useEffect(() => {
    if (isEditMode) return;
    const params = new URLSearchParams(window.location.search);
    const clubId = params.get("club");
    if (!clubId) return;
    setForm((f) => ({
      ...f,
      clubId,
      cityId: params.get("city") ?? f.cityId,
      cityName: params.get("cityName") ?? f.cityName,
    }));
  }, [isEditMode]);

  useEffect(() => {
    document.title = isEditMode ? "Edit event - Outclose" : "Create event - Outclose";
  }, [isEditMode]);

  const canSubmit = useMemo(() => {
    const capacityValid =
      form.capacity === "" ||
      (Number(form.capacity) >= 2 && Number(form.capacity) <= 500);
    return (
      form.name.trim().length >= 2 &&
      !!form.date &&
      !!form.time &&
      !!form.cityId &&
      !!form.venueName.trim() &&
      !!form.venueAddress.trim() &&
      capacityValid
    );
  }, [form]);

  // Edit-mode load gates -------------------------------------------------
  if (isEditMode && existing.isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }
  if (isEditMode && (existing.isError || (existing.data && !existing.data.success))) {
    return (
      <div className="max-w-md mx-auto p-6 text-center min-h-[60vh] flex flex-col justify-center">
        <h1 className="text-2xl font-bold mb-2">Can't edit this event</h1>
        <p className="text-neutral-500 dark:text-neutral-400 mb-6">
          {existing.data?.success === false
            ? existing.data.error || "Not found"
            : "You don't have access to edit this event."}
        </p>
        <button
          type="button"
          onClick={() => navigate(`/events/${editSlug}`)}
          className="btn btn-primary self-center px-6"
        >
          Back to event
        </button>
      </div>
    );
  }

  // Feature gate — skipped in edit mode (server already enforces access).
  if (!isEditMode) {
    if (customEventsEnabled === undefined) {
      return (
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
        </div>
      );
    }
    if (!customEventsEnabled) {
      return (
        <div className="max-w-md mx-auto p-6 text-center">
          <h1 className="text-2xl font-bold mb-2">Not available yet</h1>
          <p className="text-neutral-500 dark:text-neutral-400 mb-6">
            Hosting your own events isn't enabled on your account yet.
          </p>
          <button onClick={() => navigate("/")} className="btn btn-primary">
            Back to dashboard
          </button>
        </div>
      );
    }
  }

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const isSubmitting = create.isPending || update.isPending;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || isSubmitting) return;
    try {
      const body = toRequestBody(form);
      if (isEditMode && editSlug) {
        const res = await update.mutateAsync(body);
        if (res.success && res.data) {
          toast.success("Event updated");
          navigate(res.data.shareUrl);
        } else {
          toast.error(res.error || "Failed to update event");
        }
        return;
      }
      const res = await create.mutateAsync(body);
      if (res.success && res.data) {
        setCreated({ slug: res.data.slug, shareUrl: res.data.shareUrl });
        toast.success("Event created");
      } else {
        toast.error(res.error || "Failed to create event");
      }
    } catch (err) {
      toast.error(
        (err as Error).message ||
          (isEditMode ? "Failed to update event" : "Failed to create event"),
      );
    }
  };

  const handleCopy = async () => {
    if (!created) return;
    const url = `${window.location.origin}${created.shareUrl}`;
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Success screen ---------------------------------------------------------
  if (created) {
    const fullUrl = `${window.location.origin}${created.shareUrl}`;
    return (
      <div className="max-w-xl mx-auto p-6">
        <div className="card text-center">
          <div className="w-14 h-14 rounded-full bg-primary-500/15 flex items-center justify-center mx-auto mb-4">
            <Check className="w-7 h-7 text-primary-500" />
          </div>
          <h1 className="text-2xl font-bold mb-2">Your event is live</h1>
          <p className="text-neutral-500 dark:text-neutral-400 mb-6">
            Share this link with your guests.
          </p>

          <div className="flex items-center gap-2 mb-6">
            <input
              readOnly
              value={fullUrl}
              className="input flex-1 text-sm font-mono"
              onFocus={(e) => e.currentTarget.select()}
            />
            <button
              type="button"
              onClick={handleCopy}
              className="btn btn-primary inline-flex items-center gap-2"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? "Copied" : "Copy"}
            </button>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => navigate(created.shareUrl)}
              className="btn btn-primary flex-1"
            >
              View event
            </button>
            <button
              type="button"
              onClick={() => {
                setCreated(null);
                setForm(initialState);
              }}
              className="btn btn-secondary flex-1"
            >
              Create another
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Form -------------------------------------------------------------------
  // Single-document layout — the page reads like a poster you're filling in.
  // Naked inputs (no border, no fill) for the headline content; the icon-led
  // rows give visual rhythm without input chrome. Only the "Options" group
  // keeps card styling because it's genuinely settings, not content.

  return (
    <form onSubmit={handleSubmit} className="pb-32">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-6 space-y-4">
        {/* Cover image + title card */}
        <div className="card !p-2 space-y-3">
          <CoverImageUpload
            value={form.coverImageUrl}
            onChange={(url) => set("coverImageUrl", url)}
            onError={(msg) => toast.error(msg)}
          />
          <input
            type="text"
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            placeholder="Event name…"
            maxLength={120}
            required
            className="w-full text-3xl sm:text-4xl font-bold tracking-tight bg-transparent border-0 outline-none focus:outline-none focus:ring-0 placeholder:text-neutral-400 dark:placeholder:text-neutral-500 px-3 pb-3 pt-1"
          />
        </div>

        {/* Start / End time card */}
        <div className="card !p-0 overflow-hidden">
          <div className="px-5 py-5 flex items-center justify-between gap-3">
            <span className="font-medium">Start</span>
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={form.date}
                onChange={(e) => set("date", e.target.value)}
                required
                className={PILL_FIELD}
              />
              <input
                type="time"
                value={form.time}
                onChange={(e) => set("time", e.target.value)}
                required
                className={PILL_FIELD}
              />
            </div>
          </div>
          <div className={DIVIDER} />
          <div className="px-5 py-5 flex items-center justify-between gap-3">
            <span className="font-medium">End</span>
            {form.endTime ? (
              <div className="flex items-center gap-2">
                <input
                  type="time"
                  value={form.endTime}
                  onChange={(e) => set("endTime", e.target.value)}
                  className={PILL_FIELD}
                />
                <button
                  type="button"
                  onClick={() => set("endTime", "")}
                  className="text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300"
                  aria-label="Remove end time"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => set("endTime", form.time || "21:00")}
                className={`${PILL_FIELD} text-neutral-500 dark:text-neutral-400`}
              >
                + Add end time
              </button>
            )}
          </div>
        </div>

        {/* Where card */}
        <div className="card !p-5 space-y-3">
          <div className="flex items-start gap-3">
            <MapPin className="w-5 h-5 text-primary-500 shrink-0 mt-1.5" />
            <div className="flex-1 min-w-0">
              <button
                type="button"
                onClick={() => setCityModalOpen(true)}
                className="block w-full text-left text-base hover:text-primary-500 transition-colors"
              >
                {form.cityId ? (
                  <span>
                    <span className="mr-1">{form.cityEmoji}</span>
                    <span className="font-medium">{form.cityName}</span>
                  </span>
                ) : (
                  <span className="text-neutral-400 dark:text-neutral-500">
                    Add a city
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Venue — opens the Google-Places dialog */}
          <VenueTile
            venueName={form.venueName}
            venueAddress={form.venueAddress}
            onClick={() => setLocationModalOpen(true)}
          />
        </div>

        {/* Card 3 — Tags + description. */}
        <div className="card !p-5 space-y-4">
          {/* Tags */}
          <div className="flex items-start gap-3">
            <Tag className="w-5 h-5 text-primary-500 shrink-0 mt-1.5" />
            <div className="flex-1 flex flex-wrap items-center gap-2">
              {form.types.map((slug) => {
                const t = getEventType(slug);
                if (!t) return null;
                return (
                  <span
                    key={slug}
                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-500/15 text-primary-500 text-sm font-medium"
                  >
                    <span>{t.emoji}</span>
                    <span>{t.label}</span>
                    <button
                      type="button"
                      onClick={() =>
                        set(
                          "types",
                          form.types.filter((s) => s !== slug),
                        )
                      }
                      className="opacity-70 hover:opacity-100 -mr-1"
                      aria-label={`Remove ${t.label}`}
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </span>
                );
              })}
              <button
                type="button"
                onClick={() => setTypeModalOpen(true)}
                className="inline-flex items-center gap-1 px-3 py-1 rounded-full border border-dashed border-neutral-300 dark:border-neutral-700 text-sm text-neutral-500 dark:text-neutral-400 hover:border-primary-400 hover:text-primary-500 transition-colors"
              >
                <Plus className="w-3.5 h-3.5" />
                {form.types.length === 0
                  ? "Add tags"
                  : `${form.types.length}/${MAX_EVENT_TYPES_PER_EVENT}`}
              </button>
            </div>
          </div>

          {/* Description */}
          <RichTextEditor
            value={form.description}
            onChange={(html) => set("description", html)}
            placeholder="Tell guests what to expect — what to wear, what to bring, the vibe…"
          />

          <div className={`pt-1 ${DIVIDER}`} />

          {/* Links */}
          <LinksEditor
            links={form.links}
            onChange={(links) => set("links", links)}
          />

          {/* Host identity picker — shown when the user has groups */}
          {myGroups.length > 0 && (() => {
            const selectedGroup = myGroups.find((g) => g.id === form.groupId) ?? null;
            return (
              <>
                <div className={`pt-1 ${DIVIDER}`} />
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setGroupPickerOpen((o) => !o)}
                    className="flex items-center gap-3 w-full text-left"
                  >
                    {selectedGroup ? (
                      selectedGroup.imageUrl ? (
                        <img
                          src={selectedGroup.imageUrl}
                          alt={selectedGroup.name}
                          className="w-5 h-5 rounded object-cover shrink-0"
                        />
                      ) : (
                        <div className="w-5 h-5 rounded shrink-0 bg-gradient-to-br from-primary-500/60 via-secondary-500/50 to-accent-500/60 flex items-center justify-center">
                          <Users className="w-3 h-3 text-white/80" />
                        </div>
                      )
                    ) : (
                      <UserCircle2 className="w-5 h-5 text-primary-500 shrink-0" />
                    )}
                    <span className="flex-1 text-sm">
                      {selectedGroup ? selectedGroup.name : "Hosting as yourself"}
                    </span>
                    <ChevronDown className="w-4 h-4 text-neutral-400 shrink-0" />
                  </button>

                  {groupPickerOpen && (
                    <>
                      <div
                        className="fixed inset-0 z-40"
                        onClick={() => setGroupPickerOpen(false)}
                      />
                      <div className="absolute bottom-full left-0 right-0 mb-2 z-50 bg-white dark:bg-neutral-800 rounded-xl shadow-xl border border-neutral-200 dark:border-neutral-700 p-1 space-y-0.5">
                        <button
                          type="button"
                          onClick={() => { set("groupId", null); setGroupPickerOpen(false); }}
                          className={`flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm text-left transition-colors ${
                            !form.groupId ? "bg-primary-500/10 text-primary-600 dark:text-primary-400" : "hover:bg-base-200"
                          }`}
                        >
                          <UserCircle2 className="w-7 h-7 text-neutral-400 shrink-0" />
                          <span className="font-medium">Yourself</span>
                        </button>
                        {myGroups.map((g) => (
                          <button
                            key={g.id}
                            type="button"
                            onClick={() => { set("groupId", g.id); setGroupPickerOpen(false); }}
                            className={`flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm text-left transition-colors ${
                              form.groupId === g.id ? "bg-primary-500/10 text-primary-600 dark:text-primary-400" : "hover:bg-base-200"
                            }`}
                          >
                            {g.imageUrl ? (
                              <img
                                src={g.imageUrl}
                                alt={g.name}
                                className="w-7 h-7 rounded-lg object-cover shrink-0"
                              />
                            ) : (
                              <div className="w-7 h-7 rounded-lg shrink-0 bg-gradient-to-br from-primary-500/60 via-secondary-500/50 to-accent-500/60 flex items-center justify-center">
                                <Users className="w-3.5 h-3.5 text-white/80" />
                              </div>
                            )}
                            <span className="font-medium truncate">{g.name}</span>
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </>
            );
          })()}
        </div>

        {/* Card 4 — Options (settings). */}
        <div className="card !p-3">
          <SettingRow
            title="Price"
            description="Shown to guests on the event page"
          >
            <PriceControl
              pricing={form.pricing}
              priceDollars={form.priceDollars}
              onPricingChange={(p) => set("pricing", p)}
              onPriceChange={(v) => set("priceDollars", v)}
            />
          </SettingRow>

          <SettingRow
            title="Discoverability"
            description={
              form.visibility === "public"
                ? "Anyone can find it"
                : "Only people with the link"
            }
            isLast
          >
            <Segmented
              options={[
                { value: "public", label: "Public" },
                { value: "unlisted", label: "Unlisted" },
              ]}
              value={form.visibility}
              onChange={(v) => set("visibility", v as FormState["visibility"])}
            />
          </SettingRow>
        </div>
      </div>

      {/* Sticky submit bar */}
      <div className="max-w-2xl mx-auto py-4 px-4 pb-safe">
        <button
          type="submit"
          disabled={!canSubmit || isSubmitting}
          className="btn btn-primary w-full flex items-center justify-center gap-2 shadow-lg"
        >
          {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
          {isEditMode ? "Save changes" : "Create event"}
        </button>
      </div>

      <CitySelectModal
        isOpen={cityModalOpen}
        onClose={() => setCityModalOpen(false)}
        selectedId={form.cityId || undefined}
        onSelect={(c) => {
          set("cityId", c.id);
          set("cityName", c.name);
          set("cityEmoji", c.emoji);
        }}
      />
      <EventTypeSelectModal
        isOpen={typeModalOpen}
        onClose={() => setTypeModalOpen(false)}
        selected={form.types}
        onChange={(types) => set("types", types)}
        max={MAX_EVENT_TYPES_PER_EVENT}
      />
      <LocationSelectModal
        isOpen={locationModalOpen}
        onClose={() => setLocationModalOpen(false)}
        onSelect={async (place) => {
          set("venueName", place.name);
          set("venueAddress", place.address);
          set("venueLat", place.lat);
          set("venueLng", place.lng);
          set("venuePlaceId", place.placeId);
          // Auto-populate the closest Outclose city from the venue coords.
          if (place.lat != null && place.lng != null) {
            try {
              const res = await closestCity.mutateAsync({
                lat: place.lat,
                lng: place.lng,
              });
              if (res.success && res.data) {
                set("cityId", res.data.id);
                set("cityName", res.data.name);
                set("cityEmoji", res.data.emoji);
              }
            } catch {
              // Silent fallback — user can still pick city manually.
            }
          }
        }}
      />
    </form>
  );
}

// ---------------------------------------------------------------------------
// Subcomponents
// ---------------------------------------------------------------------------


// ---------------------------------------------------------------------------
// Consumer-app-style setting rows
// ---------------------------------------------------------------------------

function VenueTile({
  venueName,
  venueAddress,
  onClick,
}: {
  venueName: string;
  venueAddress: string;
  onClick: () => void;
}) {
  const hasVenue = !!venueName;
  return (
    <button
      type="button"
      onClick={onClick}
      className="block w-full text-left rounded-xl border border-black/10 dark:border-white/10 hover:border-primary-400 transition-colors overflow-hidden"
    >
      {hasVenue ? (
        <div className="px-4 py-3">
          <div className="font-medium">{venueName}</div>
          <div className="text-sm text-neutral-500 dark:text-neutral-400">
            {venueAddress}
          </div>
        </div>
      ) : (
        <div className="px-4 py-4 flex items-center gap-3 text-neutral-400 dark:text-neutral-500">
          <MapPin className="w-4 h-4" />
          <span>Search for a venue</span>
        </div>
      )}
    </button>
  );
}

function SettingRow({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  /** Kept for callsite ergonomics; no visual effect now that rows rely on
   *  spacing instead of inline dividers. */
  isLast?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="py-2">
      <div className="mb-3">
        <div className="font-medium">{title}</div>
        {description && (
          <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
            {description}
          </div>
        )}
      </div>
      {children}
    </div>
  );
}

function Segmented<T extends string>({
  options,
  value,
  onChange,
}: {
  options: { value: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div className="inline-flex bg-base-200 rounded-full p-1 w-full">
      {options.map((opt) => {
        const active = value === opt.value;
        return (
          <button
            type="button"
            key={opt.value}
            onClick={() => onChange(opt.value)}
            className={`
              flex-1 px-3 py-1.5 rounded-full text-sm font-medium transition-colors
              ${
                active
                  ? "bg-base-100 text-type-800 dark:text-neutral-100 shadow-sm"
                  : "text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300"
              }
            `}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}


function PriceControl({
  pricing,
  priceDollars,
  onPricingChange,
  onPriceChange,
}: {
  pricing: "" | "free" | "paid";
  priceDollars: string;
  onPricingChange: (p: "" | "free" | "paid") => void;
  onPriceChange: (v: string) => void;
}) {
  return (
    <div className="space-y-3">
      <Segmented
        options={[
          { value: "", label: "Don't say" },
          { value: "free", label: "Free" },
          { value: "paid", label: "Paid" },
        ]}
        value={pricing}
        onChange={(v) => {
          onPricingChange(v);
          if (v !== "paid") onPriceChange("");
        }}
      />
      {pricing === "paid" && (
        <div className="relative max-w-[12rem]">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500 dark:text-neutral-400 pointer-events-none">
            $
          </span>
          <input
            type="number"
            inputMode="decimal"
            min={0}
            max={10000}
            step={0.5}
            value={priceDollars}
            onChange={(e) => onPriceChange(e.target.value)}
            placeholder="0.00"
            className="input pl-8"
          />
        </div>
      )}
    </div>
  );
}

